import { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Badge } from '@tinyrewards/shared';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { earnBadge } from '../api/badges';
import { PURPLE_AVATAR } from '../theme/tokens';
import StackedCard from './StackedCard';
import { isMultiBadge } from '../constants/badgeTypes';

const EXTENSION_ID = 'aalmdggecnmihdnlloomafhmdlnjadgm';

type ChromeWindow = Window & { chrome?: { runtime?: { sendMessage?: (...args: unknown[]) => void } } };

function isYouTubeUrl(url: string): boolean {
    return /youtube\.com|youtu\.be/.test(url);
}

function goConfig(badge: Badge): { label: string; icon: React.ReactNode; disabled: boolean; tooltip: string } {
    const { badgeType, targetUrl } = badge;
    switch (badgeType) {
        case 'visit-scroll': return { label: 'Go', icon: <OpenInNewIcon fontSize="small" />, disabled: false, tooltip: 'Go to the page to earn this badge' };
        case 'watch':        return { label: 'Watch', icon: <PlayArrowIcon fontSize="small" />, disabled: false, tooltip: 'Opens on YouTube' };
        case 'geo':          return { label: 'Find It', icon: <LocationOnIcon fontSize="small" />, disabled: true, tooltip: 'Requires the mobile app' };
        case 'learningpath': return { label: 'Start', icon: <SchoolIcon fontSize="small" />,  disabled: false, tooltip: 'Begin this learning path' };
        default:
            if (targetUrl && isYouTubeUrl(targetUrl)) {
                return { label: 'Watch', icon: <PlayArrowIcon fontSize="small" />, disabled: false, tooltip: 'Watch the video to earn this badge' };
            }
            return { label: 'Earn', icon: <WorkspacePremiumIcon fontSize="small" />, disabled: false, tooltip: 'Earn this badge' };
    }
}

interface BadgeCardProps {
    badge: Badge;
    showStatus?: boolean;
    showGoButton?: boolean;
    /**
     * The signed-in user already holds this badge. Replaces the Go/Earn/Watch
     * action with an "Earned" marker — there is nothing left to do, and offering
     * the action invites a pointless round trip that the server just answers with
     * `alreadyEarned`.
     */
    isEarned?: boolean;
    isFavourite?: boolean;
    onView?: (badge: Badge) => void;
    onEdit?: (badge: Badge) => void;
    onAccess?: (badge: Badge) => void;
    onDelete?: (badge: Badge) => void;
    onToggleFavourite?: (badge: Badge) => void;
    onToggleActive?: (badge: Badge) => void;
}

function stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent ?? '';
}

export default function BadgeCard({ badge, showStatus, showGoButton, isEarned, isFavourite, onView, onEdit, onAccess, onDelete, onToggleFavourite, onToggleActive }: BadgeCardProps) {
    const plain = stripHtml(badge.badgeDescription);
    const description = plain.length > 120 ? plain.slice(0, 120) + '…' : plain;
    const { user } = useAuth();

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [earning, setEarning] = useState(false);
    const [earned, setEarned] = useState(false);
    const navigate = useNavigate();

    const handleGo = () => {
        const { badgeType, targetUrl, badgeID } = badge;
        if (badgeType === 'geo') return;

        // Earn Now (Default) badge with a YouTube presentation URL → in-app viewer
        if (badgeType === 'Default' && targetUrl && isYouTubeUrl(targetUrl)) {
            navigate(`/watch/${badgeID}`);
            return;
        }

        // Earn Now (Default) badge with no URL → self-issue immediately
        if (badgeType === 'Default' && !targetUrl) {
            setEarning(true);
            earnBadge(badgeID)
                .then((res) => {
                    if (res.success) setEarned(true);
                })
                .finally(() => setEarning(false));
            return;
        }

        // watch-type and all other URL-based badges open the target URL directly
        if (!targetUrl) return;

        // Notify the browser extension if installed, passing user details so the
        // extension knows who is logged in (mirrors the old Badges.openTargetLink behaviour)
        try {
            const cr = (window as ChromeWindow).chrome;
            cr?.runtime?.sendMessage?.(EXTENSION_ID, {
                message: 'showBadgeFromSite',
                badge: badgeID,
                url: targetUrl,
                user: user ? {
                    id: user.userID,
                    given_name: user.firstName,
                    family_name: user.lastName,
                    email: user.email,
                } : undefined,
            });
        } catch { /* extension not present */ }

        // Open the target URL and post badge info to it once loaded
        const win = window.open(targetUrl, '_blank');
        if (win && user) {
            setTimeout(() => {
                win.postMessage({ msg: 'showBadgeFromSite', badge: badgeID, uid: user.userID }, '*');
            }, 1000);
        }
    };

    const hasMenu = !!(onEdit || onAccess || onDelete);

    return (
        <StackedCard enabled={isMultiBadge(badge.badgeType)}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: badge.isActive ? 1 : 0.75, position: 'relative' }}>
            {onToggleFavourite && (
                <Tooltip title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}>
                    <IconButton
                        size="small"
                        onClick={() => onToggleFavourite(badge)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: hasMenu ? 40 : 8,
                            zIndex: 1,
                            bgcolor: 'rgba(0,0,0,0.4)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                        }}
                    >
                        {isFavourite
                            ? <FavoriteIcon fontSize="small" sx={{ color: 'accent.main' }} />
                            : <FavoriteBorderIcon fontSize="small" sx={{ color: '#fff' }} />}
                    </IconButton>
                </Tooltip>
            )}

            {hasMenu && (
                <>
                    <IconButton
                        size="small"
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                        }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={!!menuAnchor}
                        onClose={() => setMenuAnchor(null)}
                    >
                        {onEdit && (
                            <MenuItem onClick={() => { setMenuAnchor(null); onEdit(badge); }}>
                                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                        )}
                        {onAccess && (
                            <MenuItem onClick={() => { setMenuAnchor(null); onAccess(badge); }}>
                                <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Badge Access</ListItemText>
                            </MenuItem>
                        )}
                        {onDelete && (
                            <MenuItem
                                onClick={() => { setMenuAnchor(null); onDelete(badge); }}
                                sx={{ color: 'error.main' }}
                            >
                                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                <ListItemText>Delete</ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}

            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor={PURPLE_AVATAR}
                sx={{ p: 2, aspectRatio: '1', width: '100%', mx: 'auto' }}
            >
                {badge.badgeImage ? (
                    <Box
                        component="img"
                        src={badge.badgeImage}
                        alt={badge.badgeName}
                        sx={{ width: 250, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                    />
                ) : (
                    <WorkspacePremiumIcon sx={{ fontSize: 56, color: 'accent.main' }} />
                )}
            </Box>

            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom noWrap>{badge.badgeName}</Typography>
                {badge.issuerName && (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 14 }} />{badge.issuerName}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">{description}</Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                    <Chip
                        label={badge.isPublic ? 'Public' : 'Private'}
                        color={badge.isPublic ? 'success' : 'default'}
                        size="small"
                    />
                    {showStatus && (
                        <Tooltip title={onToggleActive ? (badge.isActive ? 'Click to deactivate' : 'Click to activate') : ''}>
                            <Chip
                                label={badge.isActive ? 'Active' : 'Inactive'}
                                color={badge.isActive ? 'primary' : 'error'}
                                size="small"
                                onClick={onToggleActive ? () => onToggleActive(badge) : undefined}
                                sx={onToggleActive ? { cursor: 'pointer' } : undefined}
                            />
                        </Tooltip>
                    )}
                </Box>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                {showGoButton ? (() => {
                    // Already held — there is nothing left to do, so no Go/Earn/Watch.
                    // `isEarned` is what the caller knew on load; `earned` is a badge
                    // taken in this session, so an instant-earn swaps over in place.
                    if (isEarned || earned) {
                        return (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                color="success.main"
                                data-testid="badge-earned"
                            >
                                <CheckCircleIcon fontSize="small" />
                                <Typography variant="button">Earned</Typography>
                            </Box>
                        );
                    }
                    const cfg = goConfig(badge);
                    return (
                        <Tooltip title={cfg.tooltip}>
                            <span>
                                <Button
                                    startIcon={earning ? <CircularProgress size={14} color="inherit" /> : cfg.icon}
                                    disabled={cfg.disabled || earning}
                                    onClick={handleGo}
                                >
                                    {earning ? 'Earning…' : cfg.label}
                                </Button>
                            </span>
                        </Tooltip>
                    );
                })() : (
                    <Button onClick={() => onView?.(badge)}>
                        View
                    </Button>
                )}
            </CardActions>
        </Card>
        </StackedCard>
    );
}
